import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Chip,
  Box,
  Checkbox,
  ListItemText,
  Typography,
  TextField,
  Autocomplete,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FormDiv } from '../../FormControls/styles';
import './styles.css';

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
   width: '312px',
   height: '36px',
   marginBottom: '16px !important',
  '& .MuiInputBase-root': {
    borderRadius: 6,
    backgroundColor: 'rgba(29, 104, 129, 0.04) !important',
    color: '#637177 !important',
    fontSize: 16,
    width: '312px',
    minHeight: '36px',
    height: '36px',
    color: '#637177',
    fontFamily: 'Source Sans Pro',
    padding: '6px 8px !important',
    paddingRight: '8px !important', // Extra padding for clear icon and dropdown arrow
    overflow: 'hidden',
    boxShadow: 'none',
    // transition: theme.transitions.create([
    //   'border-color',
    //   'background-color',
    //   'box-shadow',
    // ]),
     '&:hover .MuiOutlinedInput-notchedOutline': {
       borderColor: '#CED5D8 !important',
       boxShadow: 'none',
     },
     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
       borderColor: '#09779E !important',
       borderWidth: '1px !important',
       boxShadow: 'none',
     },
  },
  '& .MuiInputBase-input': {
    padding: '0 !important',
    fontFamily: 'Source Sans Pro',
    fontSize: 16,
    fontWeight: 400,
    color: '#637177 !important',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d1d5db',
  },
  '& .MuiAutocomplete-endAdornment': {
    right: 8,
    '& .MuiSvgIcon-root': {
    color: '#6b7280',
    },
  },
  '& .MuiAutocomplete-tag': {
    margin: '2px',
    maxWidth: '210px !important',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  fontSize: '0.875rem',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  backgroundColor: '#ffffff !important',
  opacity: '1 !important',
  // Prevent menu items from expanding dropdown width
  minWidth: '310px',
  maxWidth: '310px',
  minHeight: 'auto',
  height: 'auto',
  '&:hover': {
    backgroundColor: '#f9fafb !important',
  },
  '&.Mui-selected': {
    backgroundColor: '#eff6ff !important',
    '&:hover': {
      backgroundColor: '#eff6ff !important',
    },
  },
  '&.Mui-focused': {
    backgroundColor: '#f9fafb !important',
  },
}));

const SelectedChipsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(0.5),
  padding: '0',
  paddingRight: '8px', // Add padding to prevent overlap with icons
  height: '24px',
  minHeight: '24px',
  overflow: 'visible',
  flexWrap: 'nowrap',
  width: 'auto',
  maxWidth: '210px', // Leave space for dropdown arrow and prevent overlap
}));

const StyledChip = styled(Chip)(({ theme, issingle }) => ({
  height: 24,
  fontSize: '14px !important',
  backgroundColor: 'rgba(30, 105, 118, 0.08)',
  color: '#01151D',
  border: '1px solid #dbeafe',
  borderRadius: '6px',
  fontWeight: 400,
  flexShrink: 0,
  maxWidth: issingle ? '210px' : 'auto',
  '& .MuiChip-label': {
    paddingLeft: '8px',
    color: '#01151D',
    fontSize: '14px',
    fontWeight: 400,
    paddingRight: '4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: issingle ? '200px' : '150px',
  },
  '& .MuiChip-deleteIcon': {
    color: '#637177',
    fontSize: '0.875rem',
    marginLeft: '4px',
    marginRight: '4px',
    flexShrink: 0,
    '&:hover': {
      color: '#637177',
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(30, 105, 118, 0.08)',
  },
}));

const CountChip = styled(Chip)(({ theme }) => ({
  height: 24,
  fontSize: '0.75rem',
  backgroundColor: '#f3f4f6',
  color: '#6b7280',
  fontWeight: 600,
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
}));

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  '& .MuiTooltip-tooltip': {
      backgroundColor: '#4C5B62',
      color: '#FFF',
      fontSize: '14px',
      fontFamily: 'Source Sans Pro',
      fontWeight: 400,
      borderRadius: '6px',
      padding: '6px 8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
  },
  '& .MuiTooltip-arrow': {
      color: '#4C5B62',
  },
}));

const MultiSelect = ({
  label,
  options = [],
  selected = [],
  onSelect,
  placeholder = 'Select',
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  getOptionLabel = (option) => option?.label || option?.name || option,
  getOptionValue = (option) => option?.value || option?.id || option,
  maxDisplayChips = 2,
  id,
  name,
  ...props
}) => {
  const containerRef = useRef(null);
  
  // Generate unique component identifier - memoized for performance
  const componentId = useMemo(() => 
    id || `multiselect-${Math.random().toString(36).substr(2, 9)}`, [id]
  );

  // Simple logic: Calculate how many chips to show based on available width
  const getVisibleChipsCount = useCallback((selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) return 0;
    if (selectedItems.length === 1) return 1;

    // Estimate total width needed for each chip
    const chipWidthEstimates = selectedItems.slice(0, maxDisplayChips).map(item => {
      const label = getOptionLabel(item);
      // Each character ~7px + padding 16px + delete icon 20px
      return Math.min(label.length * 7 + 36, 170); // Max 170px per chip
    });

    const countChipWidth = 45; // "+n" chip width
    const chipGap = 4;
    const availableWidth = 250; // Total available space for chips

    // Try to fit chips starting from 1
    let totalWidth = chipWidthEstimates[0];
    let visibleCount = 1;

    for (let i = 1; i < chipWidthEstimates.length; i++) {
      const widthWithNextChip = totalWidth + chipGap + chipWidthEstimates[i];
      const needsCountChip = i < selectedItems.length - 1;
      const widthNeeded = needsCountChip ? widthWithNextChip + chipGap + countChipWidth : widthWithNextChip;

      if (widthNeeded <= availableWidth) {
        totalWidth = widthWithNextChip;
        visibleCount = i + 1;
      } else {
        break;
      }
    }

    return visibleCount;
  }, [getOptionLabel, maxDisplayChips]);

  // Handle autocomplete change
  const handleAutocompleteChange = useCallback((event, newValue) => {
    onSelect && onSelect(newValue || []);
  }, [onSelect]);

  // Optimized chip delete handler
  const handleChipDelete = useCallback((optionToDelete) => {
    const optionValue = getOptionValue(optionToDelete);
    const safeSelected = Array.isArray(selected) ? selected : [];
    const newSelected = safeSelected.filter(item => getOptionValue(item) !== optionValue);
    onSelect && onSelect(newSelected);
  }, [selected, getOptionValue, onSelect]);


  return (
    <FormDiv>
      <FormControl className="sols-multi-select-form-control" variant="outlined" error={error}>
        {label && (
          <InputLabel
            className="input-label multiselect-input-label"
            shrink
            htmlFor={componentId}
            required={required}
          >
            {label}
          </InputLabel>
        )}
        
        <Box ref={containerRef}>
          <StyledAutocomplete
            id={componentId}
            multiple
            options={options}
            value={selected}
            onChange={handleAutocompleteChange}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
            disabled={disabled}
            disableCloseOnSelect
            renderTags={(tagValue, getTagProps) => {
              const safeSelected = Array.isArray(tagValue) ? tagValue : [];
    if (safeSelected.length === 0) return null;

    // Calculate how many chips to show
    const visibleCount = getVisibleChipsCount(safeSelected);
    const remainingCount = safeSelected.length > visibleCount ? safeSelected.length - visibleCount : 0;
    const visibleChips = safeSelected.slice(0, visibleCount);

    return (
      <SelectedChipsContainer>
                  {visibleChips.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    const label = getOptionLabel(option);
                    return (
          <CustomTooltip title={label} key={key} arrow placement="top">
            <StyledChip
              label={label}
              issingle={safeSelected.length === 1 ? 'true' : undefined}
              onDelete={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleChipDelete(option);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              size="small"
              {...chipProps}
            />
          </CustomTooltip>
                    );
                  })}
        {remainingCount > 0 && (
          <CountChip
            key={`${componentId}-count-chip`}
            label={`+${remainingCount}`}
            size="small"
          />
        )}
      </SelectedChipsContainer>
    );
            }}
            renderOption={(props, option) => {
              const isSelected = selected.some(item => getOptionValue(item) === getOptionValue(option));
                return (
                <StyledMenuItem {...props} key={getOptionValue(option)}>
                    <ListItemText
                      primary={getOptionLabel(option)}
                      sx={{
                        margin: 0,
                        flex: 1,
                        minWidth: 0,
                        paddingRight: '8px',
                        '& .MuiListItemText-primary': {
                          color: '#01151D',
                          fontSize: '14px',
                          fontFamily: 'Source Sans Pro',
                          fontWeight: 400,
                          lineHeight: 1.4,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2, // Limits text to 2 lines before ellipsis
                          textOverflow: 'ellipsis',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                        },
                      }}
                    />
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        color: '#d1d5db',
                        padding: '4px',
                        width: '16px',
                        height: '16px',
                        flexShrink: 0,
                        marginTop: '2px',
                        '&.Mui-checked': {
                          color: '#09779E',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '16px',
                          width: '16px',
                          height: '16px',
                        },
                      }}
                    />
                  </StyledMenuItem>
                );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selected.length === 0 ? placeholder : ''}
                sx={{
                  '& .MuiInputBase-root': {
                    padding: '6px 12px !important',
                    color:'#637177',
                  },
                  '& .MuiInputBase-input': {
                    padding: '0 !important',
                  },
                }}
              />
            )}
            ListboxProps={{
              sx: {
                maxHeight: 300,
                backgroundColor: '#ffffff',
                '& .MuiAutocomplete-option': {
                  minWidth: '310px',
                  maxWidth: '310px',
                  backgroundColor: '#ffffff !important',
                  opacity: '1 !important',
                  '&:hover': {
                    backgroundColor: 'rgba(30, 105, 118, 0.08) !important',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(30, 105, 118, 0.08) !important',
                  },
                },
              },
            }}
            PaperComponent={({ children, ...other }) => (
              <Box
                {...other}
                sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  width: '310px !important',
                  minWidth: '310px !important',
                  maxWidth: '310px !important',
                  backgroundColor: '#ffffff !important',
                  opacity: 1,
                }}
              >
                {children}
              </Box>
            )}
            {...props}
          />
        </Box>

        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? 'error.main' : 'text.secondary',
              mt: 0.5,
              fontSize: '0.75rem',
            }}
          >
            {helperText}
          </Typography>
        )}
      </FormControl>
    </FormDiv>
  );
};

export default MultiSelect;